import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import type { Recipe } from '../types';
import {
    collectRecipeExternalVideoKeys,
    externalVideoKeysOverlap,
    isResolvableExternalVideoUrl,
    resolveExternalVideoIdAliasesFromUrls,
} from './externalVideoId';
import { resolveRecipeSourceUrl } from '../services/geminiService';

export function isDiscoverableSharedRecipe(recipe: Recipe): boolean {
    if (recipe.isDeleted) return false;
    return recipe.isPublic === true || (recipe.sharedWithHouseholds?.length ?? 0) > 0;
}

export function findDiscoverableRecipeInListByCandidates(
    recipes: readonly Recipe[],
    candidates: readonly string[],
    options: { excludeRecipeId?: string } = {},
): Recipe | null {
    for (const recipe of recipes) {
        if (!isDiscoverableSharedRecipe(recipe)) continue;
        if (options.excludeRecipeId && recipe.id === options.excludeRecipeId) continue;
        const recipeKeys = collectRecipeExternalVideoKeys(recipe);
        if (externalVideoKeysOverlap(candidates, [...recipeKeys])) {
            return recipe;
        }
    }
    return null;
}

export async function findDiscoverableRecipeByExternalVideoCandidates(
    candidates: readonly string[],
    options: { excludeRecipeId?: string } = {},
): Promise<Recipe | null> {
    const uniqueCandidates = [...new Set(candidates.filter((candidate) => candidate.trim()))];
    if (uniqueCandidates.length === 0) return null;

    for (const candidate of uniqueCandidates) {
        const byPrimary = await getDocs(query(collection(db, 'recipes'), where('externalVideoId', '==', candidate)));
        for (const docSnap of byPrimary.docs) {
            const recipe = { id: docSnap.id, ...docSnap.data() } as Recipe;
            if (!isDiscoverableSharedRecipe(recipe)) continue;
            if (options.excludeRecipeId && recipe.id === options.excludeRecipeId) continue;
            return recipe;
        }
    }

    for (const candidate of uniqueCandidates) {
        const byAlias = await getDocs(query(collection(db, 'recipes'), where('externalVideoIdAliases', 'array-contains', candidate)));
        for (const docSnap of byAlias.docs) {
            const recipe = { id: docSnap.id, ...docSnap.data() } as Recipe;
            if (!isDiscoverableSharedRecipe(recipe)) continue;
            if (options.excludeRecipeId && recipe.id === options.excludeRecipeId) continue;
            return recipe;
        }
    }

    return null;
}

export async function resolveExternalVideoCandidatesForUrl(url: string): Promise<{
    externalVideoId?: string;
    externalVideoIdAliases: string[];
}> {
    const trimmed = url.trim();
    if (!trimmed) return { externalVideoIdAliases: [] };

    const urlAliases = resolveExternalVideoIdAliasesFromUrls([trimmed]);
    if (!isResolvableExternalVideoUrl(trimmed)) {
        return {
            externalVideoId: urlAliases[0],
            externalVideoIdAliases: urlAliases,
        };
    }

    try {
        const resolved = await resolveRecipeSourceUrl(trimmed);
        const aliases = new Set<string>([
            ...urlAliases,
            ...(resolved.externalVideoIdAliases ?? []),
            ...(resolved.externalVideoId ? [resolved.externalVideoId] : []),
        ]);
        if (resolved.resolvedUrl) {
            resolveExternalVideoIdAliasesFromUrls([resolved.resolvedUrl]).forEach((alias) => aliases.add(alias));
        }
        const externalVideoIdAliases = [...aliases];
        return {
            externalVideoId: resolved.externalVideoId ?? externalVideoIdAliases[0],
            externalVideoIdAliases,
        };
    } catch (error) {
        console.warn('resolveRecipeSourceUrl failed:', error);
        return {
            externalVideoId: urlAliases[0],
            externalVideoIdAliases: urlAliases,
        };
    }
}

export async function findConflictingDiscoverableRecipeForUrl(
    url: string,
    options: { excludeRecipeId?: string; knownRecipes?: readonly Recipe[] } = {},
): Promise<{ externalVideoId: string; externalVideoIdAliases: string[]; recipe: Recipe } | null> {
    const resolved = await resolveExternalVideoCandidatesForUrl(url);
    if (resolved.externalVideoIdAliases.length === 0) return null;

    const fromList = options.knownRecipes
        ? findDiscoverableRecipeInListByCandidates(options.knownRecipes, resolved.externalVideoIdAliases, options)
        : null;
    if (fromList) {
        return {
            externalVideoId: resolved.externalVideoId ?? resolved.externalVideoIdAliases[0],
            externalVideoIdAliases: resolved.externalVideoIdAliases,
            recipe: fromList,
        };
    }

    const fromFirestore = await findDiscoverableRecipeByExternalVideoCandidates(resolved.externalVideoIdAliases, options);
    if (fromFirestore) {
        return {
            externalVideoId: resolved.externalVideoId ?? resolved.externalVideoIdAliases[0],
            externalVideoIdAliases: resolved.externalVideoIdAliases,
            recipe: fromFirestore,
        };
    }

    return null;
}

export async function findConflictingDiscoverableRecipeForSharing(
    externalVideoId: string | undefined,
    externalVideoIdAliases: readonly string[] | undefined,
    isPublic: boolean,
    sharedWithHouseholds: readonly string[],
    excludeRecipeId?: string,
): Promise<Recipe | null> {
    const candidates = [...new Set([
        ...(externalVideoId?.trim() ? [externalVideoId.trim()] : []),
        ...(externalVideoIdAliases ?? []).map((alias) => alias.trim()).filter(Boolean),
    ])];
    if (candidates.length === 0) return null;
    if (!isPublic && sharedWithHouseholds.length === 0) return null;
    return findDiscoverableRecipeByExternalVideoCandidates(candidates, { excludeRecipeId });
}
